import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../components/layout/MainLayout';
import MetricCard from '../../dashboard/components/MetricCard';
import UsageMetric from '../../dashboard/components/UsageMetric';
import AICallsTable from '../components/AICallsTable';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { useProfile } from '../../../hooks';
import {
  getUserCallStats,
  getUserLatestCallsPerCandidate,
  getUserCallActivityStats,
} from '../../../lib/api';
import { planHasVoiceAI } from '../../../lib/utils/planEntitlements';
import {
  CALL_ACTIVITY_PERIODS,
  CALL_ACTIVITY_PERIOD_OPTIONS,
  getCallActivitySince,
} from '../../../lib/utils/callActivityPeriod';

const EMPTY_ACTIVITY_STATS = { total: 0, noResponse: 0, openToMeet: 0, scheduledInterviews: 0 };

const AIAssistantOverview = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { profile, loading: profileLoading } = useProfile();
  const [callStats, setCallStats] = useState({ calls_duration: 0, minutes_limit: 0 });
  const [activityStats, setActivityStats] = useState(EMPTY_ACTIVITY_STATS);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityPeriod, setActivityPeriod] = useState(CALL_ACTIVITY_PERIODS.TODAY);
  const hasLoadedRef = useRef(false);

  const plan = profile?.subscription_plan?.toLowerCase() || null;
  const hasVoiceAI = planHasVoiceAI(plan);
  const extraMinutes = Math.max(0, Number(profile?.extra_minutes) || 0);

  const activitySince = useMemo(
    () => getCallActivitySince(activityPeriod),
    [activityPeriod],
  );

  const periodOptions = useMemo(
    () => CALL_ACTIVITY_PERIOD_OPTIONS.map((option) => ({
      ...option,
      label: t(`ai.overview.periods.${option.value}`),
    })),
    [t],
  );

  useEffect(() => {
    const fetchOverviewData = async () => {
      if (!userId || (profileLoading && !profile)) return;

      if (!hasVoiceAI) {
        setLoading(false);
        hasLoadedRef.current = false;
        return;
      }

      const useSilent = hasLoadedRef.current;

      try {
        if (!useSilent) setLoading(true);
        const [stats, nextActivityStats, userCalls] = await Promise.all([
          getUserCallStats(userId),
          getUserCallActivityStats(userId, { since: activitySince }),
          getUserLatestCallsPerCandidate(userId),
        ]);
        setCallStats(stats || { calls_duration: 0, minutes_limit: 0 });
        setActivityStats(nextActivityStats || EMPTY_ACTIVITY_STATS);
        setCalls(userCalls || []);
        hasLoadedRef.current = true;
      } catch (error) {
        console.error('Error fetching AI assistant overview data:', error);
      } finally {
        if (!useSilent) setLoading(false);
      }
    };

    fetchOverviewData();
  }, [userId, hasVoiceAI, profileLoading, profile, activitySince]);

  const metrics = useMemo(() => [
    {
      id: 1,
      title: t('ai.overview.callsMade'),
      value: String(activityStats.total),
      icon: 'Phone',
    },
    {
      id: 2,
      title: t('ai.overview.noResponse'),
      value: String(activityStats.noResponse),
      icon: 'PhoneOff',
    },
    {
      id: 3,
      title: t('ai.overview.openToMeet'),
      value: String(activityStats.openToMeet),
      icon: 'Handshake',
    },
    {
      id: 4,
      title: t('ai.overview.scheduledInterviews'),
      value: String(activityStats.scheduledInterviews),
      icon: 'Calendar',
    },
  ], [activityStats, t]);

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
            {t('ai.title')}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('ai.subtitle')}
          </p>
        </div>

        {loading || (profileLoading && !profile) ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : hasVoiceAI ? (
          <>
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 md:mb-6">
                <h2 className="text-lg md:text-xl font-semibold text-foreground">
                  {t('ai.overview.callActivity')}
                </h2>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                    {t('ai.overview.showing')}
                  </span>
                  <div className="w-full sm:w-56">
                    <Select
                      options={periodOptions}
                      value={activityPeriod}
                      onChange={setActivityPeriod}
                      placeholder={t('ai.overview.selectPeriod')}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {metrics.map((metric) => (
                  <MetricCard
                    key={metric.id}
                    title={metric.title}
                    value={metric.value}
                    icon={metric.icon}
                  />
                ))}
              </div>
            </div>

            <UsageMetric
              title={t('ai.overview.monthlyMinutes')}
              current={callStats.calls_duration || 0}
              total={(callStats.minutes_limit || 0) + extraMinutes}
              unit="minutes"
              color="warning"
              footnote={
                extraMinutes > 0
                  ? t('ai.overview.extraMinutesNote', { count: extraMinutes })
                  : null
              }
            />
            <AICallsTable calls={calls} showSectionHeader />
          </>
        ) : null}
      </div>
    </MainLayout>
  );
};

export default AIAssistantOverview;
